import {
  Button,
  Center,
  createStyles,
  Divider,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  useMantineTheme,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { ITrackedEquipment, ITrackedEquipmentRepair } from '../../../../models';
import { useEffect, useState } from 'react';
import {
  useIsAdmin,
  useIsTechnician,
  useTrackedEquipmentRepairByTrackedEquipment,
} from '../../../../hooks';
import dayjs from 'dayjs';
import { ArrowLeft, Hammer, Search } from 'tabler-icons-react';
import {
  InfiniteScrollArea,
  Th,
  TrackedEquipmentDetailSection,
  TrackedEquipmentPatientSection,
} from '../../../../components';

const useStyles = createStyles((theme) => ({
  notesCard: {
    border: `1px solid ${theme.colors.gray[4]}`,
    borderRadius: theme.radius.sm,
    paddingLeft: theme.spacing.sm,
    paddingRight: theme.spacing.sm,
    paddingTop: 3,
    width: '100%',
    height: '125px',
  },
  infoBox: {
    color: `${theme.colors.gray[6]}`,
    border: `1px solid ${theme.colors.gray[6]}`,
    borderRadius: theme.radius.lg,
    width: 'fit-content',
    paddingLeft: '10px',
    paddingRight: '10px',
  },
  groupLabel: {
    color: theme.colors.gray[5],
    fontSize: theme.fontSizes.sm,
    whiteSpace: 'pre',
  },
  fieldLabel: {
    color: theme.colors.gray[5],
    fontSize: theme.fontSizes.xs,
    whiteSpace: 'pre',
  },
  editLabel: {
    fontSize: theme.fontSizes.xs,
    fontWeight: 500,
    whiteSpace: 'pre',
  },
  detail: {
    fontSize: theme.fontSizes.md,
    paddingLeft: 10,
  },
  fieldGroup: {
    width: '48%',
  },
  clickableIcon: {
    color: `${theme.colors.blue[6]}`,
    padding: '0 !important',
  },
  buttonText: {
    [`@media (max-width: ${theme.breakpoints.sm})`]: {
      fontSize: 12,
    },
  },
}));

function filterData(data: ITrackedEquipmentRepair[], search: string) {
  if (data && data.length > 0) {
    const keys = Object.keys(data[0]);
    const query = search.toLowerCase().trim();
    return data.filter((item) =>
      keys.some((key) => (item[key] ?? '-').toString().toLowerCase().includes(query))
    );
  } else {
    return [];
  }
}

function sortData(
  data: ITrackedEquipmentRepair[],
  payload: { sortBy: keyof ITrackedEquipmentRepair; reversed: boolean; search: string }
) {
  if (!payload.sortBy) {
    return filterData(data, payload.search);
  }

  return filterData(
    [...data].sort((a, b) => {
      if (payload.reversed) {
        return (b[payload.sortBy] ?? '000ForceToBottom')
          .toString()
          .localeCompare((a[payload.sortBy] ?? '000ForceToBottom').toString());
      }

      return (a[payload.sortBy] ?? '!!!ForceToTop').localeCompare(
        (b[payload.sortBy] ?? '!!!ForceToTop').toString()
      );
    }),
    payload.search
  );
}

type TrackedEquipmentRepairHistoryModalProps = {
  trackedEquipment: ITrackedEquipment;
  repairCreated: boolean;
  repairDeleted: boolean;
  opened: boolean;
  onSelect: (repair: ITrackedEquipmentRepair) => void;
  onRepair: () => void;
  onClose: () => void;
};

const TrackedEquipmentRepairHistoryModal = (props: TrackedEquipmentRepairHistoryModalProps) => {
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const isSmallMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);

  const isAdmin = useIsAdmin().data?.isAdmin;
  const isTechnician = useIsTechnician().data?.isTechnician;

  const repairRecords = useTrackedEquipmentRepairByTrackedEquipment(
    props.trackedEquipment.trackedEquipmentId
  );

  const [search, setSearch] = useState('');
  const [sortedData, setSortedData] = useState(repairRecords.data);
  const [sortBy, setSortBy] = useState<keyof ITrackedEquipmentRepair>(null!);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);

  const setSorting = (field: keyof ITrackedEquipmentRepair) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    setSortedData(sortData(repairRecords.data!, { sortBy: field, reversed, search }));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearch(value);
    setSortedData(
      sortData(repairRecords.data!, {
        sortBy,
        reversed: reverseSortDirection,
        search: value,
      })
    );
  };

  const rows = (sortedData ?? []).map((a: ITrackedEquipmentRepair) => {
    return (
      <tr
        key={a.trackedEquipmentRepairId}
        style={{ cursor: 'pointer' }}
        onClick={() => {
          props.onSelect(a);
        }}
      >
        <td style={{ wordWrap: 'break-word', width: '25%' }}>
          {dayjs(a.sentDateTime).format('YYYY-MM-DD')}
        </td>
        <td style={{ wordWrap: 'break-word', width: '25%' }}>
          {a.returnedDateTime ? dayjs(a.returnedDateTime).format('YYYY-MM-DD') : '-'}
        </td>
      </tr>
    );
  });

  useEffect(() => {
    if (!repairRecords.data) {
      setSortedData(repairRecords.data);
    } else {
      setSortedData(
        sortData(repairRecords.data, { sortBy, reversed: reverseSortDirection, search })
      );
    }
  }, [repairRecords.data, reverseSortDirection, search, sortBy]);

  useEffect(() => {
    if (props.opened) {
      repairRecords.refetch();
    }
  }, [props.trackedEquipment, props.opened]);

  return (
    <Modal
      title={
        <Text style={{ color: 'green' }}>
          {props.repairCreated
            ? 'Repair Record Successfully Created!'
            : props.repairDeleted
            ? 'Repair Record Successfully Deleted!'
            : ''}
        </Text>
      }
      opened={props.opened}
      size={isSmallMobile ? 'xs' : 'md'}
      onClose={() => {
        props.onClose();
      }}
    >
      <Stack spacing={0} style={{ position: 'relative' }}>
        <Center>
          <Text size="xl" weight={500} align="center">
            Equipment Repairs
          </Text>
        </Center>
        {props.trackedEquipment?.inNeedOfRepair &&
          !props.trackedEquipment?.outForRepairs &&
          !props.trackedEquipment.retired &&
          !props.trackedEquipment?.ready && (
            <Center>
              <Group
                className={classes.infoBox}
                position="center"
                spacing="xs"
                my={5}
                style={{ borderColor: theme.colors.red[6] }}
              >
                <Text weight={500} size="xs" style={{ color: theme.colors.red[6] }}>
                  Equipment in Need of Repair{' '}
                  <Hammer style={{ marginBottom: -2 }} size={theme.fontSizes.sm} strokeWidth={2} />
                </Text>
              </Group>
            </Center>
          )}
        {props.trackedEquipment?.outForRepairs &&
          !props.trackedEquipment.retired &&
          !props.trackedEquipment?.ready && (
            <Center>
              <Group
                className={classes.infoBox}
                position="center"
                spacing="xs"
                my={5}
                style={{ borderColor: theme.colors.blue[6] }}
              >
                <Text weight={500} size="xs" style={{ color: theme.colors.blue[6] }}>
                  Equipment Out For Repairs{' '}
                  <Hammer style={{ marginBottom: -2 }} size={theme.fontSizes.sm} strokeWidth={2} />
                </Text>
              </Group>
            </Center>
          )}
        {props.trackedEquipment.checkedOutDate && !props.trackedEquipment.outForRepairs && (
          <TrackedEquipmentPatientSection
            trackedEquipment={props.trackedEquipment}
            header="Current Patient"
          />
        )}
        <TrackedEquipmentDetailSection trackedEquipment={props.trackedEquipment} readOnly />
        <Center mt={theme.spacing.xs}>
          <Text size="md" weight={500} align="center">
            Cleaning History
          </Text>
        </Center>
        <Divider />
        <TextInput
          placeholder="Search by any field"
          my="xs"
          icon={<Search size={14} />}
          value={search}
          onChange={handleSearchChange}
        />
        <Divider />
        <Table style={{ width: '100%', tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <Th
                sorted={sortBy === 'sentDateTime'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('sentDateTime')}
                style={{ width: '45%' }}
              >
                Date Sent
              </Th>
              <Th
                sorted={sortBy === 'returnedDateTime'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('returnedDateTime')}
                style={{ width: '45%' }}
              >
                Date Returned
              </Th>
            </tr>
          </thead>
        </Table>
        <InfiniteScrollArea rows={rows} height={250} />
        <Divider />
        <Group spacing="xs" grow noWrap mt="xs">
          <Button
            variant="outline"
            p={0}
            onClick={() => {
              props.onClose();
            }}
          >
            <ArrowLeft style={{ marginRight: 5 }} />
            Return to Details
          </Button>
          {(isAdmin || isTechnician) && (
            <Button
              variant="outline"
              disabled={
                props.trackedEquipment?.checkedOutDate === undefined ||
                props.trackedEquipment.retired ||
                props.trackedEquipment.outForRepairs
              }
              p={0}
              onClick={() => {
                props.onRepair();
              }}
            >
              <Hammer style={{ marginRight: 5 }} />
              New Repair
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
};

export default TrackedEquipmentRepairHistoryModal;
